import type { CellState, RunState } from './types';

/** next[] markers. */
export const EXIT = -1;
export const NONE = -2;

export const INF = 1 << 30;
export const COST_EMPTY = 10;
/** Stepping into a live structure means breaking it first; the field treats that as a very long detour. */
export const COST_STRUCTURE = 210;

export interface Field {
  /** Cost remaining after leaving the cell. INF = unreachable. */
  dist: number[];
  /** One optimal next cell, EXIT at a goal, NONE if unreachable. Use optimalNext() to see all ties. */
  next: number[];
}

/** Cost of stepping INTO cell i. INF = impassable. */
export type EnterCost = (i: number) => number;

export function enemyEnterCost(grid: readonly CellState[], structureAlive: (i: number) => boolean): EnterCost {
  return (i) => {
    const c = grid[i]!;
    if (c.kind === 'closed' || c.kind === 'base') return INF;
    if (c.kind === 'unit') return structureAlive(i) ? COST_STRUCTURE : COST_EMPTY;
    return COST_EMPTY;
  };
}

export function soldierEnterCost(grid: readonly CellState[], structureAlive: (i: number) => boolean): EnterCost {
  return (i) => {
    const c = grid[i]!;
    if (c.kind === 'closed' || c.kind === 'base') return INF;
    if (c.kind === 'unit') return structureAlive(i) ? INF : COST_EMPTY;
    return COST_EMPTY;
  };
}

/** Up, left, right, down. Order matters for ties; never change it casually. */
export function neighbors(width: number, height: number, i: number): number[] {
  const x = i % width;
  const y = Math.floor(i / width);
  const out: number[] = [];
  if (y > 0) out.push(i - width);
  if (x > 0) out.push(i - 1);
  if (x < width - 1) out.push(i + 1);
  if (y < height - 1) out.push(i + width);
  return out;
}

/**
 * Dijkstra over the grid from a set of goal cells (stepping off the board from a goal costs 0).
 * Integer costs, fixed neighbour order, strict "<" relaxation: fully deterministic.
 * Boards are tiny (<= 60 cells), so the O(n^2) selection is fine and allocation-free logic is simpler.
 */
export function flowField(width: number, height: number, goals: readonly number[], cost: EnterCost): Field {
  const n = width * height;
  const dist: number[] = new Array<number>(n).fill(INF);
  const next: number[] = new Array<number>(n).fill(NONE);
  const done: boolean[] = new Array<boolean>(n).fill(false);
  for (const g of goals) {
    if (cost(g) < INF) {
      dist[g] = 0;
      next[g] = EXIT;
    }
  }
  for (let iter = 0; iter < n; iter++) {
    let best = -1;
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i]! < INF && (best === -1 || dist[i]! < dist[best]!)) best = i;
    }
    if (best === -1) break;
    done[best] = true;
    const viaBest = cost(best) + dist[best]!;
    for (const ni of neighbors(width, height, best)) {
      if (done[ni] || cost(ni) >= INF) continue;
      if (viaBest < dist[ni]!) {
        dist[ni] = viaBest;
        next[ni] = best;
      }
    }
  }
  return { dist, next };
}

/**
 * Every equally cheap continuation from a cell: neighbours n with cost(n) + dist[n] == dist[cur].
 * At a goal the only answer is [EXIT]. Empty when the cell is unreachable.
 */
export function optimalNext(field: Field, cost: EnterCost, width: number, height: number, cur: number): number[] {
  if (field.next[cur] === EXIT) return [EXIT];
  const d = field.dist[cur]!;
  if (d >= INF) return [];
  const out: number[] = [];
  for (const ni of neighbors(width, height, cur)) {
    const c = cost(ni);
    if (c >= INF) continue;
    if (c + field.dist[ni]! === d) out.push(ni);
  }
  return out;
}

export function bottomRow(width: number, height: number): number[] {
  const out: number[] = [];
  for (let x = 0; x < width; x++) out.push((height - 1) * width + x);
  return out;
}

export function topRow(width: number): number[] {
  const out: number[] = [];
  for (let x = 0; x < width; x++) out.push(x);
  return out;
}

export function baseCells(grid: readonly CellState[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < grid.length; i++) if (grid[i]!.kind === 'base') out.push(i);
  return out;
}

/**
 * Where enemies want to stand to hit the base: the passable cells touching the base building,
 * or the bottom row when the map has no base cells (then the base is the bottom edge itself).
 */
export function enemyGoals(grid: readonly CellState[], width: number, height: number): number[] {
  const base = baseCells(grid);
  if (base.length === 0) return bottomRow(width, height);
  const set = new Set<number>();
  for (const b of base) {
    for (const ni of neighbors(width, height, b)) {
      const k = grid[ni]!.kind;
      if (k !== 'base' && k !== 'closed') set.add(ni);
    }
  }
  return [...set].sort((a, b) => a - b);
}

export interface PathPreview {
  /** Columns enemies can enter from. */
  spawnColumns: number[];
  /**
   * Corridor cells: everything within `slack` of the cheapest full route from any spawn column.
   * One array (kept as a list of lists for the renderer). Wider than the exact routes on purpose,
   * so one building on one side does not make the whole other side vanish.
   */
  routes: number[][];
  /** Cells on an exactly optimal route that hold a live structure: enemies will break them. */
  breakCells: number[];
}

/** Cheapest cost to reach and enter each cell from the spawn edge (multi-source Dijkstra). */
export function forwardField(width: number, height: number, sources: readonly number[], cost: EnterCost): number[] {
  const n = width * height;
  const dist: number[] = new Array<number>(n).fill(INF);
  const done: boolean[] = new Array<boolean>(n).fill(false);
  for (const s of sources) {
    const c = cost(s);
    if (c < INF && c < dist[s]!) dist[s] = c;
  }
  for (let iter = 0; iter < n; iter++) {
    let best = -1;
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i]! < INF && (best === -1 || dist[i]! < dist[best]!)) best = i;
    }
    if (best === -1) break;
    done[best] = true;
    for (const ni of neighbors(width, height, best)) {
      if (done[ni]) continue;
      const c = cost(ni);
      if (c >= INF) continue;
      const nd = dist[best]! + c;
      if (nd < dist[ni]!) dist[ni] = nd;
    }
  }
  return dist;
}

/** One cell of slack: a detour that short still reads as "the enemy may come this way"; more reads as terrain. */
export const CORRIDOR_SLACK = 10;

/** Where enemies would walk given the current board. Used by the client during placement. */
export function previewPaths(state: RunState): PathPreview {
  const alive = (i: number): boolean => state.grid[i]!.kind === 'unit';
  const cost = enemyEnterCost(state.grid, alive);
  const field = flowField(state.width, state.height, enemyGoals(state.grid, state.width, state.height), cost);
  return tracePaths(state.width, state.height, field, cost, alive);
}

export function tracePaths(
  width: number,
  height: number,
  field: Field,
  cost: EnterCost,
  structureAlive: (i: number) => boolean,
  slack: number = CORRIDOR_SLACK,
): PathPreview {
  const spawnColumns: number[] = [];
  const spawnCells: number[] = [];
  for (let x = 0; x < width; x++) {
    if (cost(x) >= INF || field.dist[x]! >= INF) continue;
    spawnColumns.push(x);
    spawnCells.push(x);
  }
  if (spawnCells.length === 0) return { spawnColumns, routes: [], breakCells: [] };

  // Exact routes: breadth-first over optimal edges from every spawn column.
  const exact = new Set<number>();
  const queue = [...spawnCells];
  for (const s of spawnCells) exact.add(s);
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const nx of optimalNext(field, cost, width, height, cur)) {
      if (nx === EXIT || exact.has(nx)) continue;
      exact.add(nx);
      queue.push(nx);
    }
  }
  const breakCells = [...exact].filter((i) => structureAlive(i)).sort((a, b) => a - b);

  // Corridor: cells whose best full route is within `slack` of the global best.
  const fwd = forwardField(width, height, spawnCells, cost);
  let bestTotal = INF;
  for (let i = 0; i < width * height; i++) {
    if (fwd[i]! < INF && field.dist[i]! < INF) bestTotal = Math.min(bestTotal, fwd[i]! + field.dist[i]!);
  }
  const corridor: number[] = [];
  for (let i = 0; i < width * height; i++) {
    if (fwd[i]! < INF && field.dist[i]! < INF && fwd[i]! + field.dist[i]! <= bestTotal + slack) corridor.push(i);
  }
  return { spawnColumns, routes: [corridor], breakCells };
}
